<script lang="ts" setup>
import MarkdownIt from 'markdown-it'
import DOMPurify from 'dompurify'
import { useReleaseNoteStore } from '@/stores/releaseNoteStore'
import { useRouter } from 'vue-router'

const releaseNoteStore = useReleaseNoteStore()
const router = useRouter()

const md = new MarkdownIt({ html: false, linkify: true, typographer: true })

const currentSlide = ref(0)

const items = computed(() => releaseNoteStore.unseenItems)
const isFirst = computed(() => currentSlide.value === 0)
const isLast = computed(() => currentSlide.value === items.value.length - 1)

const apiBase = (import.meta.env.VITE_API_URL as string || '').replace(/\/$/, '')

function resolveImageUrl(imageUrl: string): string {
  if (!imageUrl) return ''
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) return imageUrl
  return `${apiBase}${imageUrl}`
}

function renderMarkdown(content: string): string {
  return DOMPurify.sanitize(md.render(content))
}

function navigateCta(url: string): void {
  if (!url) return
  // Close the carousel first (marks seen in normal mode, exits preview in preview mode)
  onGotIt()
  // Always use Vue Router — CTAs point to internal app routes
  router.push(url)
}

function onDialogUpdate(value: boolean): void {
  if (!value) {
    if (releaseNoteStore.previewMode) {
      releaseNoteStore.exitPreview()
    } else {
      releaseNoteStore.closeWithoutMarkSeen()
    }
  }
}

function onGotIt(): void {
  if (releaseNoteStore.previewMode) {
    releaseNoteStore.exitPreview()
  } else {
    releaseNoteStore.markSeen()
  }
}

watch(
  () => releaseNoteStore.showCarousel,
  (visible) => {
    if (visible) {
      currentSlide.value = 0
    }
  },
)
</script>

<template>
  <v-dialog
    :model-value="releaseNoteStore.showCarousel"
    width="50vw"
    persistent
    @update:model-value="onDialogUpdate"
  >
    <v-card>
      <v-card-text
        v-if="releaseNoteStore.previewMode"
        class="pa-2 d-flex justify-center"
      >
        <v-chip
          color="warning"
          size="small"
          prepend-icon="mdi-eye"
          variant="tonal"
        >
          Preview Mode
        </v-chip>
      </v-card-text>

      <v-window v-model="currentSlide" @touchstart.stop>
        <v-window-item
          v-for="(item, index) in items"
          :key="item.id"
          :value="index"
        >
          <v-card-text
            class="pt-4"
            style="max-height: calc(50vh - 120px); overflow-y: auto;"
          >
            <div class="d-flex align-center mb-3">
              <v-chip
                color="primary"
                size="small"
              >
                v{{ item.version }}
              </v-chip>
            </div>

            <v-img
              v-if="item.imageUrl"
              :src="resolveImageUrl(item.imageUrl)"
              max-height="200"
              contain
              class="mb-3 rounded"
            />

            <v-card-title class="px-0 pt-0 pb-2 text-h6">
              {{ item.title }}
            </v-card-title>

            <!-- eslint-disable-next-line vue/no-v-html -->
            <div
              class="release-carousel__markdown"
              v-html="renderMarkdown(item.content)"
            />

            <div
              v-if="item.ctaLabel && item.ctaUrl"
              class="mt-3"
            >
              <v-btn
                variant="outlined"
                @click="navigateCta(item.ctaUrl)"
              >
                {{ item.ctaLabel }}
              </v-btn>
            </div>
          </v-card-text>
        </v-window-item>
      </v-window>

      <v-divider />
      <v-card-actions class="px-4 pb-4 d-flex align-center justify-space-between">
        <v-btn
          variant="tonal"
          prepend-icon="mdi-chevron-left"
          :disabled="isFirst"
          @click="currentSlide--"
        >
          Previous
        </v-btn>

        <span class="text-caption text-medium-emphasis">
          {{ currentSlide + 1 }} / {{ items.length }}
        </span>

        <v-btn
          v-if="!isLast"
          color="primary"
          variant="tonal"
          append-icon="mdi-chevron-right"
          @click="currentSlide++"
        >
          Next
        </v-btn>

        <v-btn
          v-if="isLast"
          color="primary"
          variant="flat"
          append-icon="mdi-check"
          @click="onGotIt()"
        >
          Got it!
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

